import { parseFlightDate } from './utils';

import type { NetSkyscannerArrivaldeparturesvcV1ArrivalDepartureService as ArrivalDepartureTypes } from '@skyscanner-internal/arrival-departure-svc-web-http-client';

type FlightStatusUpdate = ArrivalDepartureTypes.FlightStatusUpdate;

/**
 * Format a UTC date into a YYYY-MM-DD key.
 * Used only for flights that have no localised departure field available.
 */
const formatUTCDateKey = (date: Date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Match the YYYY-MM-DD prefix of a localised datetime string
 * (e.g. "2026-04-23T01:15:00" - airport-local wall clock, no TZ suffix).
 */
const localisedDateKeyPattern = /^(\d{4}-\d{2}-\d{2})/;

const extractLocalisedDateKey = (value: string | undefined) => {
  if (!value) return '';

  const match = localisedDateKeyPattern.exec(value);
  return match ? match[1] : '';
};

/**
 * Extract the calendar date a flight belongs to, expressed in the
 * departure-airport local timezone whenever possible (UAT 3.1 / CASTLE-4926).
 *
 * Preference chain (airport-local beats UTC at each step, a departure time
 * beats an arrival time, scheduled beats estimated/actual):
 *  1. `localisedScheduledDepartureTime` (airport-local wall clock)
 *  2. `scheduledDepartureTime` (UTC)
 *  3. `localisedEstimatedDepartureTime`
 *  4. `estimatedDepartureTime` (UTC)
 *  5. `localisedActualDepartureTime`
 *  6. `actualDepartureTime` (UTC)
 *  7-12. Same sequence for the arrival fields, used only when every
 *        departure field is absent.
 *
 * Arrival times are only consulted as a last resort because an overnight
 * red-eye can arrive on a different calendar day in a different timezone,
 * which would otherwise mis-bucket the flight onto the wrong chip.
 */
export const getFlightDateKey = (flight: FlightStatusUpdate) => {
  const preferenceOrder: Array<{
    localised: string | undefined;
    utc: string | undefined;
  }> = [
    {
      localised: flight.localisedScheduledDepartureTime,
      utc: flight.scheduledDepartureTime,
    },
    {
      localised: flight.localisedEstimatedDepartureTime,
      utc: flight.estimatedDepartureTime,
    },
    {
      localised: flight.localisedActualDepartureTime,
      utc: flight.actualDepartureTime,
    },
    {
      localised: flight.localisedScheduledArrivalTime,
      utc: flight.scheduledArrivalTime,
    },
    {
      localised: flight.localisedEstimatedArrivalTime,
      utc: flight.estimatedArrivalTime,
    },
    {
      localised: flight.localisedActualArrivalTime,
      utc: flight.actualArrivalTime,
    },
  ];

  for (const { localised, utc } of preferenceOrder) {
    const localisedKey = extractLocalisedDateKey(localised);
    if (localisedKey) {
      return localisedKey;
    }

    if (utc) {
      const parsed = parseFlightDate(utc);
      if (parsed) {
        return formatUTCDateKey(parsed);
      }
    }
  }

  return '';
};

export const isDivertedFlight = (
  flight: FlightStatusUpdate | null | undefined,
) => Boolean(flight?.isDiverted);

/**
 * Detect if a flight number already includes a carrier code prefix (e.g., "BA123").
 * New API format always includes carrier code; old format may not.
 */
const hasCarrierCodePrefix = (flightNumber: string): boolean =>
  // Pattern: 2 uppercase letters followed by 1-4 digits
  /^[A-Z]{2}\d{1,4}$/.test(flightNumber);
export const getDisplayFlightCode = (
  flight: FlightStatusUpdate,
  requestedCarrierCode?: string,
) => {
  const flightNumber = flight.flightNumber || '';

  // Prefer opFlightNumber if available (takes precedence over all logic)
  if (flight.opFlightNumber) {
    return flight.opFlightNumber;
  }

  // If flightNumber already has a carrier code prefix, return as-is
  if (hasCarrierCodePrefix(flightNumber)) {
    return flightNumber;
  }

  // Otherwise, conditionally prepend requestedCarrierCode for legacy format
  if (requestedCarrierCode && !flightNumber.startsWith(requestedCarrierCode)) {
    return `${requestedCarrierCode}${flightNumber}`;
  }

  return flightNumber;
};

export const getScheduledArrivalAirportCode = (flight: FlightStatusUpdate) =>
  flight.scheduledArrivalAirportCode || flight.arrivalAirportCode;

export const getScheduledArrivalAirportName = (flight: FlightStatusUpdate) =>
  flight.scheduledArrivalAirportName || flight.arrivalAirportName;
